/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { Box, Button, Container, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import * as Sentry from "@sentry/react";
import { useLazyGetBanksByCodeQuery, useLazyGetIncomeDocumentsConfigQuery } from "@api/catalogs";
import {
  useDeleteBankAccountMutation,
  useDeleteBankAccountStatementMutation,
  useLazyGetBankAccountsQuery,
  usePostBankAccountsMutation,
  usePostBankAccountStatementMutation,
  usePostValidateNextStageMutation,
} from "@api/applications";
import { useLazyGetDocumentAppIdQuery, usePatchDocumentMutation } from "@api/documents";
import { CustomDialog } from "@components/CustomDialog";
import { InactivityHandler } from "@components/InactivityHandler";
import { ProgressBar } from "@components/ProgressBar";
import CustomRightImage from "@components/CustomRightImagev2";
import CustomUploadFileV2 from "@components/CustomUploadFileV2";
import {
  BOFU_TABS_CONFIG,
  DOCUMENT_VALIDATION_TYPES,
  DOCUMENTS_CATALOG,
  STATUS_CATALOG,
  TAX_SYSTEM_TYPE
} from "@helpers/constantes";
import { identifyBankAccounts } from "@helpers/funciones";
import useExpedienteDocuments from "@hooks/useExpedienteDocuments";
import useStatusConfig from "@hooks/useStatusConfig";
import useTofuSteps from "@hooks/useTofuSteps";
import { BankAccountFormItem, BofuTabDocument, DocumentResponse, PayloadBankAccount } from "@interfaces/index";
import { ApiError } from "@interfaces/response";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { setLoanStatus } from "@store/slices/registerSlice";
import { RootState, useAppDispatch } from "@store/store";
import { UUID } from "node:crypto";
import { generateDocumentConfig, validateDocumentStructure } from "@components/TabBofu/functions";
import DocumentsTabBofu from "@components/TabBofu/DocumentsTabBofu";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import { CiecValidatorRequest, useCiecValidatorMutation } from "@api/sat_apis_new/satApsi";

const SCREEN_STATUS = "CSDOS";
const BANKCODE = "999";
const MAX_ACCOUNTS_ALLOWED = 3;

const createDefaultBankAccount = (
  numberOfBankStatements: number,
  bankId: string,
  bankName: string,
): BankAccountFormItem => ({
  id: "",
  clabe: "000000000000000000",
  bank_id: bankId,
  bank_name: bankName,
  statements: Array.from({ length: numberOfBankStatements }, (_, index) => ({
    bank_statement_document_id: undefined,
    document_id: "",
    document_order: index + 1,
    document_value: undefined,
  })),
});

const normalizeStatements = (
  statements: Array<BankAccountFormItem["statements"][number] | undefined> | undefined,
  targetCount: number,
): BankAccountFormItem["statements"] => {
  const current = Array.isArray(statements) ? [...statements] : [];
  return Array.from({ length: targetCount }, (_, index) => {
    const existing = current[index];
    if (existing) {
      return {
        bank_statement_document_id: existing.bank_statement_document_id,
        document_id: existing.document_id ?? "",
        document_order: index + 1,
        document_value: existing.document_value,
      };
    }

    return {
      bank_statement_document_id: undefined,
      document_id: "",
      document_order: index + 1,
      document_value: undefined,
    };
  });
};

type FormValues = {
  accounts: BankAccountFormItem[];
};

const DocumentosCiec = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);
  const { nextStatus: naiveNextStatus } = useStatusConfig(SCREEN_STATUS);
  const { nextStatus: afterCiecStatus } = useStatusConfig(STATUS_CATALOG.consulta_ciec.code);
  // Según el producto, CSDOS (documentos_sat) puede tener o no un paso de buró (ej. CSABE)
  // antes de CIEC. Si el siguiente paso natural ya es CIEC, se salta hasta lo que sigue después
  // de CIEC (la rama de documentos no debe pasar por esa pantalla); si el siguiente paso natural
  // es otro (ej. CSABE), se respeta tal cual, sin tocar la secuencia del backend.
  const nextStatus = naiveNextStatus?.code === STATUS_CATALOG.consulta_ciec.code ? afterCiecStatus : naiveNextStatus;
  const interfaceConfig = useInterfaceConfig();

  const applicationData = useSelector((state: RootState) => state.register.application);
  const personalData = useSelector((state: RootState) => state.register.personal_data);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const brand = useSelector((state: RootState) => state.brand);
  const companyData = useSelector((state: RootState) => state.register.company);

  const [getBankAccounts] = useLazyGetBankAccountsQuery();
  const [getDocumentsByAppId] = useLazyGetDocumentAppIdQuery();
  const [triggerGetBanks] = useLazyGetBanksByCodeQuery();
  const [triggerPostBankAccount] = usePostBankAccountsMutation();
  const [triggerPatchDocument] = usePatchDocumentMutation();
  const [triggerPostBankAccountStatement] = usePostBankAccountStatementMutation();
  const [triggerDeleteBankAccountStatement] = useDeleteBankAccountStatementMutation();
  const [deleteBankAccount] = useDeleteBankAccountMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [triggerGetIncomeDocumentsConfig] = useLazyGetIncomeDocumentsConfigQuery();
  const [ciecValidation] = useCiecValidatorMutation();

  const isCreatingInitialAccount = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loadingBankStatement, setLoadingBankStatement] = useState(false);
  const [accountIndexToDelete, setAccountIndexToDelete] = useState<number | undefined>(undefined);
  const [bankId, setBankId] = useState("");
  const [bankName, setBankName] = useState("");

  const DEFAULT_NUMBER_OF_BANK_STATEMENTS =
    interfaceConfig?.formularios?.informacion_bancaria_empresa?.numero_estados_cuenta || 3;

  const formik = useFormik<FormValues>({
    initialValues: {
      accounts: [createDefaultBankAccount(DEFAULT_NUMBER_OF_BANK_STATEMENTS, "", "")],
    },
    validationSchema: yup.object().shape({
      accounts: yup
        .array()
        .of(
          yup.object().shape({
            statements: yup
              .array()
              .of(
                yup.object().shape({
                  document_id: yup.string().required("Documento requerido."),
                  document_order: yup.number().required().min(1).max(DEFAULT_NUMBER_OF_BANK_STATEMENTS),
                }),
              )
              .test(
                "all-statements-uploaded",
                "Debes cargar todos los estados de cuenta.",
                (value) =>
                  Array.isArray(value) &&
                  value.length === DEFAULT_NUMBER_OF_BANK_STATEMENTS &&
                  value.every((statement) => Boolean(statement?.document_id)),
              ),
          }),
        )
        .min(1, "Debes cargar al menos una cuenta bancaria."),
    }),
    onSubmit: async (_, { setSubmitting }) => {
      try {
        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }
        setSubmitting(true);

        const payload_ciec: CiecValidatorRequest = {
          rfc: companyData.rfc || "",
          ciec: "",
          application_code: applicationData.company_application_code,
          client_details_id: String(applicationData.client_details_id),
          loan_application_conditions_id: String(applicationData.application_condition_id),
          loan_application_id: String(applicationData.id),
          requested_amount: Number(applicationData.loan_amount),
          requested_term: Number(applicationData.loan_term),
          validate_ciec: false,
          company_id: brand?.id ?? "",
          connection_is_mobile_app: true,
        };

        if (
          applicationData.tax_system_type === TAX_SYSTEM_TYPE.PF.code ||
          applicationData.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code
        ) {
          await ciecValidation(payload_ciec).unwrap();
        }

        await triggerPostNextStage({
          applicationId: applicationData.id,
          statusCode: nextStatus.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        dispatch(setLoanStatus(nextStatus.code));
        navigate(nextStatus.url);
      } catch (error) {
        const apiError = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "DocumentosCiec-submit",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: apiError }));
      } finally {
        setSubmitting(false);
      }
    },
    enableReinitialize: false,
    validateOnMount: true,
  });

  const createInitialAccount = async (index = 0, bankIdOverride?: string, bankNameOverride?: string) => {
    try {
      if (isCreatingInitialAccount.current) {
        return;
      }

      isCreatingInitialAccount.current = true;

      let effectiveBankId = bankIdOverride ?? bankId;
      let effectiveBankName = bankNameOverride ?? bankName;

      if (!effectiveBankId) {
        const bankInfo = await triggerGetBanks(BANKCODE).unwrap();
        effectiveBankId = bankInfo?.id || "";
        effectiveBankName = bankInfo?.name || "";
        setBankId(effectiveBankId);
        setBankName(effectiveBankName);
      }

      if (!effectiveBankId) {
        throw { message: "No se pudo obtener el banco para crear la cuenta inicial." } as ApiError;
      }

      const payload: PayloadBankAccount = {
        loan_application_id: String(applicationData.id),
        client_details_id: personalData.id,
        account: 0,
        account_holder: "",
        person_id: null,
        clabe: "000000000000000000",
        bank_id: effectiveBankId as UUID,
        documents: [],
      };

      const response = await triggerPostBankAccount({
        solicitudId: applicationData.id,
        body: payload,
      }).unwrap();

      formik.setFieldValue(`accounts[${index}].id`, response.id);
      formik.setFieldValue(`accounts[${index}].bank_id`, effectiveBankId);
      formik.setFieldValue(`accounts[${index}].bank_name`, effectiveBankName);
    } catch (error) {
      const apiError = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "DocumentosCiec-createInitialAccount",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      isCreatingInitialAccount.current = false;
    }
  };

  const createAccount = async (index: number, bankIdOverride?: string, bankNameOverride?: string) => {
    try {
      setIsLoading(true);

      let effectiveBankId = bankIdOverride ?? bankId;
      let effectiveBankName = bankNameOverride ?? bankName;

      if (!effectiveBankId) {
        const bankInfo = await triggerGetBanks(BANKCODE).unwrap();
        effectiveBankId = bankInfo?.id || "";
        effectiveBankName = bankInfo?.name || "";
        setBankId(effectiveBankId);
        setBankName(effectiveBankName);
      }

      if (!effectiveBankId) {
        throw { message: "No se pudo obtener el banco para crear la cuenta." } as ApiError;
      }

      const payload: PayloadBankAccount = {
        loan_application_id: String(applicationData.id),
        client_details_id: personalData.id,
        account: 0,
        account_holder: "",
        person_id: null,
        clabe: `000000000000000000${index + 1}`,
        bank_id: effectiveBankId as UUID,
        documents: [],
      };

      const response = await triggerPostBankAccount({
        solicitudId: applicationData.id,
        body: payload,
      }).unwrap();

      formik.setFieldValue(`accounts[${index}].id`, response.id);
      formik.setFieldValue(`accounts[${index}].bank_id`, effectiveBankId);
      formik.setFieldValue(`accounts[${index}].bank_name`, effectiveBankName);
    } catch (error) {
      const apiError = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "DocumentosCiec-createAccount",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddAccount = async () => {
    try {
      if (formik.values.accounts.length >= MAX_ACCOUNTS_ALLOWED) {
        return;
      }

      let resolvedBankId = bankId;
      let resolvedBankName = bankName;

      if (!resolvedBankId) {
        const bankInfo = await triggerGetBanks(BANKCODE).unwrap();
        resolvedBankId = bankInfo?.id || "";
        resolvedBankName = bankInfo?.name || "";
        setBankId(resolvedBankId);
        setBankName(resolvedBankName);
      }

      if (!resolvedBankId) {
        throw { message: "No se pudo obtener el banco para crear la cuenta." } as ApiError;
      }

      formik.setFieldValue("accounts", [
        ...formik.values.accounts,
        createDefaultBankAccount(DEFAULT_NUMBER_OF_BANK_STATEMENTS, resolvedBankId, resolvedBankName),
      ]);

      await createAccount(formik.values.accounts.length, resolvedBankId, resolvedBankName);
    } catch (error) {
      const apiError = error as ApiError;
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    }
  };

  const handleUploadBankStatement = async (
    newUpload: DocumentResponse | undefined,
    accountIndex: number,
    statementIndex: number,
  ) => {
    try {
      setLoadingBankStatement(true);

      const copyAccounts = [...formik.values.accounts];
      const account = copyAccounts[accountIndex];
      const statement = account.statements[statementIndex];

      if (newUpload) {
        if (account.id) {
          const response = await triggerPostBankAccountStatement({
            solicitudId: applicationData.id,
            accountId: account.id,
            body: {
              loan_application_bank_account_id: account.id,
              document_id: newUpload.id,
              document_order: statementIndex + 1,
            },
          }).unwrap();

          copyAccounts[accountIndex].statements[statementIndex] = {
            bank_statement_document_id: response.id,
            document_id: newUpload.id,
            document_order: statementIndex + 1,
            document_value: newUpload,
          };

          formik.setFieldValue("accounts", copyAccounts);
        }
      } else if (account.id && statement?.bank_statement_document_id) {
        await triggerDeleteBankAccountStatement({
          solicitudId: applicationData.id,
          accountId: account.id,
          documentId: statement.bank_statement_document_id,
        }).unwrap();

        copyAccounts[accountIndex].statements[statementIndex] = {
          bank_statement_document_id: undefined,
          document_id: "",
          document_order: statementIndex + 1,
          document_value: undefined,
        };

        formik.setFieldValue("accounts", copyAccounts);
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "DocumentosCiec-handleUploadBankStatement",
        },
      });
    } finally {
      setLoadingBankStatement(false);
    }
  };

  const handleOpenModalDelete = (index: number) => {
    setAccountIndexToDelete(index);
  };

  const handleOnCloseModalDelete = () => {
    setAccountIndexToDelete(undefined);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);

      if (accountIndexToDelete === undefined) {
        return;
      }

      const copyAccounts = [...formik.values.accounts];
      const accountToDelete = copyAccounts[accountIndexToDelete];

      if (!accountToDelete || !accountToDelete.id) {
        formik.setFieldValue(
          "accounts",
          copyAccounts.filter((_, index) => index !== accountIndexToDelete),
        );
        setAccountIndexToDelete(undefined);
        return;
      }

      for (let index = 0; index < accountToDelete.statements.length; index++) {
        const bankStatement = accountToDelete.statements[index];

        if (bankStatement?.document_id) {
          await triggerPatchDocument({
            documentId: bankStatement.document_id as `${string}-${string}-${string}-${string}-${string}`,
            body: {
              status: false,
            },
          }).unwrap();
        }
      }

      await deleteBankAccount({
        solicitudId: applicationData.id,
        accountId: accountToDelete.id,
      }).unwrap();

      formik.setFieldValue(
        "accounts",
        copyAccounts.filter((_, index) => index !== accountIndexToDelete),
      );
      handleOnCloseModalDelete();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "DocumentosCiec-handleConfirmDelete",
        },
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const { expedienteDocuments, setExpedienteDocuments, handleUpdateDocument, handleUpdateDocumentMultiple } =
    useExpedienteDocuments();

  const getInitialData = async () => {
    try {
      setIsLoading(true);

      const [documents, bankAccounts, bankInfo, incomeDocumentsConfig] = await Promise.all([
        getDocumentsByAppId({
          loanApplicationId: applicationData.id,
          clientDetailsId: personalData.id,
          status: true,
        }).unwrap(),
        getBankAccounts(applicationData.id).unwrap(),
        triggerGetBanks(BANKCODE).unwrap(),
        triggerGetIncomeDocumentsConfig({ tax_systems_id: applicationData.tax_system_id as string }).unwrap(),
      ]);

      // filtrar los documentos asignados al client_details
      const docs_account = documents.filter((doc) => doc.client_details_id === applicationData.client_details_id);

      const valid_tab_documents: BofuTabDocument[] = [];

      // filtar documentos con configuracion valida para el formulario
      incomeDocumentsConfig?.documents
        .filter((item) => validateDocumentStructure(item))
        .forEach((docItem) => {
          const document_value = generateDocumentConfig({
            docItem,
            docs_account,
            docs_aval: [],
            clientDetailsId: applicationData.client_details_id as UUID,
            aval: undefined,
          });

          valid_tab_documents.push(document_value);
        });

      const resolvedBankId = bankInfo?.id || "";
      const resolvedBankName = bankInfo?.name || "";

      setBankId(resolvedBankId);
      setBankName(resolvedBankName);
      setExpedienteDocuments(valid_tab_documents);

      const { company_accounts } = identifyBankAccounts({
        bank_accounts: bankAccounts,
        company: personalData,
      });

      const accounts: BankAccountFormItem[] = company_accounts.map((account) => {
        const normalizedAccount: BankAccountFormItem = {
          id: account.id,
          clabe: account.clabe || "",
          bank_id: account.bank_id || resolvedBankId,
          bank_name: account.bank_name || resolvedBankName,
          statements: normalizeStatements(undefined, DEFAULT_NUMBER_OF_BANK_STATEMENTS),
        };

        for (let statementIndex = 0; statementIndex < DEFAULT_NUMBER_OF_BANK_STATEMENTS; statementIndex++) {
          const accountDocument = account.documents[statementIndex];

          if (accountDocument) {
            normalizedAccount.statements[statementIndex] = {
              bank_statement_document_id: accountDocument.bank_statement_document_id,
              document_id: accountDocument.document_id,
              document_order: accountDocument.document_order || statementIndex + 1,
              document_value: documents.find((item) => item.id === accountDocument.document_id),
            };
          }
        }

        return normalizedAccount;
      });

      if (accounts.length > 0) {
        formik.setFieldValue("accounts", accounts, false);
      } else {
        formik.setFieldValue(
          "accounts",
          [createDefaultBankAccount(DEFAULT_NUMBER_OF_BANK_STATEMENTS, resolvedBankId, resolvedBankName)],
          false,
        );
        await createInitialAccount(0, resolvedBankId, resolvedBankName);
      }
      setTimeout(async () => {
        await formik.validateForm();
      }, 500);
    } catch (error) {
      const apiError = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "DocumentosCiec-getInitialData",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: apiError }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getInitialData();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />

        <Box
          sx={{
            mt: 2,
            mx: { xs: 2, md: 4 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box maxWidth="sm" width="100%">
            <Typography variant="overline" fontWeight={700} letterSpacing={1.6} color="primary.main">
              EMPRESA · DOCUMENTOS
            </Typography>

            <Typography variant="tofu_title" textAlign="left" color="primary.dark">
              Sube los documentos de tu empresa
            </Typography>

            <Typography variant={isMobileOrTablet ? "body2" : "body1"} mt={1} color="primary.dark">
              Necesitamos tus estados de cuenta bancarios para evaluar tu solicitud de crédito.
            </Typography>
          </Box>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3, px: "0 !important" }}>
            <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
          </Container>

          <Container maxWidth="sm" sx={{ mt: 3, mb: 2, px: "0 !important" }}>
            <form onSubmit={formik.handleSubmit}>
              <DocumentsTabBofu
                inputPrefix={`cliente`}
                tabCode={BOFU_TABS_CONFIG.tab_representante_pf.code}
                documents={expedienteDocuments}
                onChange={handleUpdateDocument}
                onChangeMultiple={handleUpdateDocumentMultiple}
              />

              <Box maxWidth="sm" width="100%" mt={2}>
                <Typography variant="h5" color="primary.dark" fontWeight={700} mt={1}>
                  Estados de cuenta bancarios
                </Typography>

                <Typography variant={isMobileOrTablet ? "body2" : "body1"} mt={1} color="primary.dark">
                  Carga los últimos 3 estados de la cuenta donde recibes al menos el 60% de tu facturación. Si recibes
                  ingresos en varias cuentas, sube 3 estados de cada una.
                </Typography>
              </Box>

              {formik.values.accounts.map((account, accountIndex) => (
                <Box key={`bank-account-${accountIndex}`} mt={3}>
                  <Typography variant={isMobileOrTablet ? "body2" : "body1"} fontWeight={700} color="primary.dark">
                    Cuenta bancaria {accountIndex + 1}
                  </Typography>

                  {account.statements.map((statement, statementIndex) => (
                    <CustomUploadFileV2
                      key={`bank_statement_${accountIndex}_${statementIndex}`}
                      mt={2}
                      inputName={`bank_statement_${accountIndex}_${statementIndex}`}
                      uploadedFile={statement?.document_value}
                      setUploadedFile={(newUpload) => {
                        handleUploadBankStatement(newUpload, accountIndex, statementIndex);
                      }}
                      optionalName={`Estado de cuenta ${statementIndex + 1}`}
                      optionalDescription=""
                      metadata={{
                        fileCode: DOCUMENTS_CATALOG.estado_cuenta_bancario.code,
                        uploadType: "client",
                        clientDetailsId: personalData.id,
                      }}
                      disabled={isLoading || isDeleting || loadingBankStatement || formik.isSubmitting || !account.id}
                      validation_type={DOCUMENT_VALIDATION_TYPES.estado_cuenta_bancario}
                      required
                    />
                  ))}

                  {accountIndex > 0 && (
                    <Typography
                      onClick={() => handleOpenModalDelete(accountIndex)}
                      mt={2}
                      color="primary.main"
                      sx={{
                        cursor:
                          isLoading || isDeleting || formik.values.accounts[accountIndex].id === undefined
                            ? "not-allowed"
                            : "pointer",
                      }}
                    >
                      - Eliminar cuenta bancaria
                    </Typography>
                  )}
                </Box>
              ))}

              <Typography
                onClick={handleAddAccount}
                mt={3}
                color="primary.main"
                sx={{ cursor: formik.values.accounts.length >= MAX_ACCOUNTS_ALLOWED ? "not-allowed" : "pointer" }}
              >
                + Agregar otra cuenta bancaria
              </Typography>

              <Container maxWidth="sm" sx={{ mb: 2, mt: 2, px: "0 !important" }}>
                <Box display="flex" justifyContent="center">
                  <Button
                    variant="default"
                    color="primary"
                    type="submit"
                    disabled={
                      isLoading ||
                      isDeleting ||
                      loadingBankStatement ||
                      formik.isSubmitting ||
                      !formik.isValid ||
                      formik.values.accounts.some((account) => !account.id)
                    }
                  >
                    Continuar
                  </Button>
                </Box>
              </Container>
            </form>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}

      <CustomDialog
        colorClose={theme.palette.primary.light}
        open={accountIndexToDelete !== undefined}
        onClose={handleOnCloseModalDelete}
        itemTitle={<></>}
        itemContent={
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
            <Typography
              textAlign="center"
              color="primary.dark"
              fontWeight={600}
              variant={isMobileOrTablet ? "h5" : "h6"}
            >
              ¿Estás seguro de eliminar la cuenta bancaria?
            </Typography>

            <Box display="flex" columnGap={2} justifyContent="center" mt={3}>
              <Button
                variant="blue_outlined"
                sx={{ py: 0, px: 1, width: "110px" }}
                onClick={handleOnCloseModalDelete}
                disabled={isDeleting}
              >
                Regresar
              </Button>

              <Button variant="default" sx={{ width: "110px" }} onClick={handleConfirmDelete} disabled={isDeleting}>
                Eliminar
              </Button>
            </Box>
          </Box>
        }
      />
    </Grid>
  );
};

export default DocumentosCiec;
